/*
  "What is blocking <project>?" (UI overhaul wave 3, screen 04 idea 9).

  Always shows up to 3 points computed from the project's own data (blockersModel.buildBlockers) — instantly,
  with the AI switched off. The AI wording is OFF by default (it sends project facts to Google): only when the
  `ai_extras` feature is switched on (Admin › Integrations) AND the server switch AI_PROJECT_FEATURES_ENABLED is set
  (useProjectAi) does the card ask
  the server for the model's wording of the same facts (GET /projects/{id}/blockers/ai, cached per project
  state) and show it with an "AI" badge; while that loads, or if it fails, the computed points stay.
  "Ask a follow-up" opens the AI page with ?project=…&ask=…: the AI page focuses on this project and puts the
  question in its box (not sent until the person asks). It shows whenever the AI page itself can be used — the
  `ai` integration on and a role with the AI page — even with AI extras or the project-page switch off (those only
  guard the model's wording here, which sends project facts to Google).
  Colours are the theme's lime tone tokens (--status-lime / -bg / -ink), so the dark theme gets its own.
*/
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, MessageSquarePlus, Sparkles } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatTimeAgo } from '@/lib/format';
import { isFeatureNotEnabledError } from '@/lib/integrations';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { Panel } from './ProjectUi';
import { useProjectAi } from './useProjectAi';

const NUMBER_TONE = {
  red: 'bg-status-red-bg text-status-red-ink',
  amber: 'bg-status-amber-bg text-status-amber-ink',
  lime: 'bg-status-lime-bg text-status-lime-ink',
  green: 'bg-status-green-bg text-status-green-ink',
};

export function BlockersCard({
  projectId,
  projectName,
  points = [],
  basis = ['stages', 'execution'],
  refreshKey,
  canAskAi = false,
  className,
}) {
  const navigate = useNavigate();
  const projectAi = useProjectAi();
  const aiOn = projectAi.enabled;
  // the AI page only needs the `ai` integration (and the role); the project switch is about the wording above
  const canFollowUp = Boolean(projectAi.aiOn) && canAskAi;
  const [aiState, setAiState] = useState({ status: 'idle', points: [], generatedAt: '' });
  const [computedAt] = useState(() => new Date().toISOString());

  useEffect(() => {
    if (!aiOn || !projectId) {
      setAiState({ status: 'idle', points: [], generatedAt: '' });
      return undefined;
    }
    let cancelled = false;
    setAiState((prev) => ({ ...prev, status: 'loading' }));
    api.get(`/projects/${projectId}/blockers/ai`)
      .then((res) => {
        if (cancelled) return;
        const aiPoints = Array.isArray(res.data?.points) ? res.data.points.filter(Boolean).slice(0, 3) : [];
        setAiState({ status: aiPoints.length ? 'ready' : 'error', points: aiPoints, generatedAt: res.data?.generated_at || '', basis: Array.isArray(res.data?.basis) ? res.data.basis : null });
      })
      .catch((err) => {
        if (!cancelled) setAiState({ status: isFeatureNotEnabledError(err) ? 'idle' : 'error', points: [], generatedAt: '' });
      });
    return () => { cancelled = true; };
  }, [aiOn, projectId, refreshKey]);

  const showAi = aiState.status === 'ready';
  const items = showAi ? aiState.points.map((point, index) => ({ key: `ai-${index}`, tone: index === 0 ? 'red' : 'amber', text: point })) : points;
  const shownBasis = showAi && aiState.basis?.length ? aiState.basis : basis;
  const basisText = shownBasis.length > 1 ? `${shownBasis.slice(0, -1).join(', ')} and ${shownBasis[shownBasis.length - 1]}` : shownBasis[0];
  const askText = `What is blocking ${projectName || 'this project'} and who needs to act?`;

  return (
    <Panel
      className={cn('overflow-hidden border-[color:color-mix(in_srgb,var(--status-lime)_40%,var(--surface))] shadow-[0_0_0_3px_var(--status-lime-bg)]', className)}
      data-testid="stages-blockers"
      aria-labelledby="stages-blockers-title"
    >
      <div className="flex min-h-[52px] items-center gap-2.5 border-b border-line px-4 py-2.5 sm:px-5">
        <span className="ui-icon-tile tone-lime h-8 w-8 shrink-0" aria-hidden="true">
          <Sparkles className="h-4 w-4" />
        </span>
        <h3 id="stages-blockers-title" className="min-w-0 flex-1 text-[14px] font-bold leading-snug text-ink">
          What is blocking {projectName || 'this project'}?
        </h3>
        {showAi ? <StatusPill tone="lime" size="sm" label="AI" hideDot data-testid="stages-blockers-ai" /> : null}
        {aiState.status === 'loading' ? <Loader2 className="h-3.5 w-3.5 animate-spin text-ink-4" aria-label="Asking the AI assistant" /> : null}
      </div>
      <ol className="space-y-2.5 px-4 py-3.5 sm:px-5" aria-live="polite">
        {items.map((point, index) => (
          <li key={point.key} className="flex gap-2.5 text-[13px] leading-[1.5] text-ink-2" data-testid={`stages-blocker-${point.key}`}>
            <span className={cn('mt-[1px] grid h-5 w-5 shrink-0 place-items-center rounded-full text-[11px] font-bold', NUMBER_TONE[point.tone] || 'bg-surface-2 text-ink-3')} aria-hidden="true">
              {index + 1}
            </span>
            <span className="min-w-0">
              {point.parts
                ? point.parts.map((part, partIndex) => (part.strong
                  ? <b key={partIndex} className="font-semibold text-ink">{part.text}</b>
                  : <span key={partIndex}>{part.text}</span>))
                : point.text}
            </span>
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line bg-surface-2 px-4 py-2.5 sm:px-5">
        <span className="text-[12px] leading-snug text-ink-3">
          Based on {basisText} · {formatTimeAgo(showAi ? (aiState.generatedAt || computedAt) : computedAt)}
          {aiState.status === 'error' ? ' · AI wording unavailable' : ''}
        </span>
        {canFollowUp ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              // The AI page focuses on this project and puts the question in the box (not sent until asked).
              navigate(`/ai?project=${encodeURIComponent(projectId)}&ask=${encodeURIComponent(askText)}`);
            }}
            data-testid="stages-blockers-ask"
          >
            <MessageSquarePlus aria-hidden="true" />
            Ask a follow-up
          </Button>
        ) : null}
      </div>
    </Panel>
  );
}
